import { Injectable } from '@angular/core';
import { BullhornAttachmentSource } from 'attachment-source-lib';
import { BullhornDataSourceApiService } from 'datasource-service-lib';
import { IEntity, IEntityPathService, INamedEntity } from 'interfaces-lib';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { UtilsService } from 'utils-service-lib';

export interface IEntityRelation {
  id?: string;
  name?: string;
  entityType: string;
  childEntities?: IEntityRelation[];
}

@Injectable({ providedIn: 'root' })
export class BullhornEntityPathService implements IEntityPathService {
  constructor(
    private datasourceService: BullhornDataSourceApiService,
    private bullhornAttachmentService: BullhornAttachmentSource
  ) {}

  getEntitiesFromPaths(entity: INamedEntity, entityPaths: string[], secondaryEntity?: INamedEntity): Observable<INamedEntity[]> {
    entityPaths = entityPaths?.length ? entityPaths : [this.bullhornAttachmentService.getDefault(entity.type)];
    const _entity = secondaryEntity ?? entity;
    const entities: IEntityRelation = this.populate(_entity, entityPaths);
    return this.populateChildEntityIds(entities).pipe(
      map((_) => this.flattenRelations(entities, [])),
      map((value) => this.showOnlyLeafs(entityPaths, value))
    );
  }

  private populateChildEntityIds(entity: IEntityRelation): Observable<IEntityRelation> {
    if (!entity.childEntities || entity.childEntities.length === 0) {
      return of(entity);
    }

    const fields = entity.childEntities.map((child) => UtilsService.lowerCaseFirstLetter(child.entityType)).join(',');

    const iEntity: IEntity = { type: entity.entityType, id: (entity.id as string) };
    return this.datasourceService.get(iEntity, { fields: fields }).pipe(
      switchMap((responseData) => {
        const childEntityObservables = entity.childEntities?.map((child) => {
          const entityType = UtilsService.lowerCaseFirstLetter(child.entityType);
          const childData = responseData[entityType];

          if (!childData || !childData.id) {
            return of(child);
          }

          child.id = childData.id;
          child.name = this.normaliseName(childData);
          return this.populateChildEntityIds(child);
        });

        return forkJoin((childEntityObservables as Observable<IEntityRelation>[])).pipe(map(() => entity));
      })
    );
  }

  private showOnlyLeafs(entityPaths: string[], namedEntities: INamedEntity[]): INamedEntity[] {
    const leafs = entityPaths.map((value) => value.split('.').pop());
    return namedEntities.filter((entity) => leafs.includes(entity.type));
  }

  private flattenRelations(entity: IEntityRelation, flatEntities: INamedEntity[]): INamedEntity[] {
    // move each entity to the namedEntities array
    flatEntities.push({ name: (entity.name as string), id: (entity.id as string), type: entity.entityType });
    entity.childEntities?.forEach((childEntity) => {
      this.flattenRelations(childEntity, flatEntities);
    });
    return flatEntities;
  }

  private normaliseName(bhEntity: any) {
    // candidate / contact = firstName+lastname
    // clientCorporation = name
    // jobOrder = title
    return bhEntity.title ?? bhEntity.name ?? bhEntity.firstName + ' ' + bhEntity.lastName;
  }

  private populate(topLevelEntity: INamedEntity, entityPathArrays: string[]) {
    // Placement.Candidate
    // Placement.JobOrder.ClientCorporation
    // Placement.JobOrder.ClientContact

    const entityFields: IEntityRelation = {
      id: topLevelEntity.id,
      name: topLevelEntity.name,
      entityType: topLevelEntity.type,
      childEntities: [],
    };

    entityPathArrays.forEach((entityPath) => {
      this.createRelation(entityFields, entityPath.split('.'));
    });
    return entityFields;
  }

  private createRelation(parent: IEntityRelation, path: string[]): IEntityRelation {
    const current = path.shift();
    if (current === undefined) {
      return parent;
    } else if (current === parent.entityType) {
      this.createRelation(parent, path);
    } else {
      const child = this.getChild(current, (parent.childEntities as IEntityRelation[]));
      if (child) {
        this.createRelation(child, path);
      } else {
        const newChild: IEntityRelation = { entityType: current, childEntities: [] };
        parent.childEntities?.push(newChild);
        this.createRelation(newChild, path);
      }
    }
    return parent;
  }

  private getChild(childName: string, childEntities: IEntityRelation[]) {
    return childEntities.find((child) => child.entityType === childName);
  }
}
